import * as React from 'react';
import { cn } from '@/shared/lib/utils';

interface ToggleSwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

const ToggleSwitch = ({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  className,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: ToggleSwitchProps) => {
  const id = React.useId();

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onCheckedChange(!checked);
      }
    },
    [checked, disabled, onCheckedChange],
  );

  return (
    <div className={cn('flex items-center justify-between gap-4', className)} data-testid={testId}>
      {}
      {(label || description) ? (
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {label ? (
            <label
              htmlFor={id}
              className="block cursor-pointer select-none text-form-label text-label"
            >
              {label}
            </label>
          ) : null}
          {description ? (
            <span className="text-sm leading-snug text-muted-foreground">
              {description}
            </span>
          ) : null}
        </div>
      ) : null}

      {}
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        data-th-button="true"
        tabIndex={0}
        disabled={disabled}
        onClick={() => !disabled && onCheckedChange(!checked)}
        onKeyDown={handleKeyDown}
        className={cn(
          'relative inline-flex h-[22px] w-[40px] flex-shrink-0 cursor-pointer items-center rounded-full border-0 p-[2px]',
          'transition-colors duration-200 ease-in-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2',
          checked ? 'bg-primary' : 'bg-gray-200',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        {}
        <span
          aria-hidden="true"
          style={{ transform: checked ? 'translateX(18px)' : 'translateX(0)' }}
          className={cn(
            'pointer-events-none inline-block h-[18px] w-[18px] rounded-full bg-white shadow-sm',
            'transition-transform duration-200 ease-in-out',
          )}
        />
      </button>
    </div>
  );
};

export default ToggleSwitch;
